import { GameActions, IGameLoadData } from '../../State/Game/GameActions';
import Game from '../../Game';
import { ShortcutStatus } from '../../State/User/UserActions';
import { setServerDate } from '../../Utility/DateHelper';
import {
    ResourceDriver,
    ResourceEvent,
} from '../../Utility/Resource/ResourceManager';
import { FBTrackingEvent } from '../../Utility/Tracking/FBTrackingEvent';
import { IAPNotSupportedError } from '../../Utility/Error/IAPNotSupported';
import { isIOS } from 'mobile-device-detect';
import { captureException } from '../../Sentry';
import System from '../System';
import { IGameState } from '../../State/Game/IGameState';
import { UIActions } from '../../State/UI/UIActions';
import FontFaceObserver from 'fontfaceobserver';
import { PlatformActions } from '../../State/Game/PlatformActions';

export default class PreloadSystem extends System {
    private gameLoadedState: IGameLoadData;
    private showGDPR: boolean;

    public init(): void {}

    public async preload() {
        // Try to sign the user in silently
        Game.state.dispatch(PlatformActions.signIn(true));

        Game.resource.add('styles', 'resource/data/styles.json');
        Game.resource.add('no_avatar', 'resource/graphics/other/no_avatar.png');
        Game.resource.add('level_stars', 'resource/data/level_stars.json');

        Game.resource.add(
            'atlas_ui_common',
            'resource/graphics/export/atlas_ui_common.json'
        );
        Game.resource.add('atlas_hd', 'resource/graphics/export/atlas_hd.json');

        // Dragonbones transitions screen.
        Game.resource.add(
            'ScreenTransitions_skeleton_data',
            'resource/dragonbones/transition/skeleton.json'
        );
        Game.resource.add(
            'ScreenTransitions_texture',
            'resource/dragonbones/transition/texture.png'
        );
        Game.resource.add(
            'ScreenTransitions_texture_data',
            'resource/dragonbones/transition/texture.json'
        );
        const onProgress = (progress: number) => {
            Game.sdk.loadingProgress(progress);
        };

        Game.resource.getDriver().on(ResourceEvent.PROGRESS, onProgress);

        this.prepareIAP().catch(e => {
            // Ignore IAPNotSupportedError.
            if (!(e instanceof IAPNotSupportedError)) {
                if (isIOS) {
                    console.error(e);
                } else {
                    captureException(e);
                }
            }
        });
        // Load font and catch timeout exception (or other errors).
        try {
            await new FontFaceObserver('RussoOne').load();
        } catch (e) {
            console.error(e);
        }

        try {
            await new FontFaceObserver('Corp').load();
        } catch (e) {
            console.error(e);
        }

        // Load preload assets and player data simultaneously.
        await Promise.all([Game.resource.load(), Game.player.data.restore()]);

        Game.resource.getDriver().off(ResourceEvent.PROGRESS, onProgress);

        Game.style.setStyleData(Game.resource.get('styles'));

        // Immediate create backup after player-restore see (if does not exist already).
        await Game.sdk.prepareMigrationBackup(
            Game.player.data.playerEndpointData
        );

        // Save the current game version, once per session.
        await Game.sdk.save({ version: Game.config.version });

        // Trigger loadingComplete.
        // await Game.sdk.loadingComplete();

        let restoredPlayerState = Game.player.data.getPlayerState();

        // TODO: Only required for FB tracking, remove when tracking refactored.
        Game.player.FTUE = restoredPlayerState.FTUE;

        // Do not wait for the GBC when the game initializes.
        // However, if the GBC is already connected by the time the assets are loaded we check and attempt to restore
        // lost player progress.
        if (await Game.sdk.isGBCConnected()) {
            restoredPlayerState = await Game.sdk.restoreLostPlayerProgress(
                restoredPlayerState
            );
        }

        const language = Game.sdk.getLanguage();

        // Set the language after SDK loadingComplete.
        Game.locale.setLanguage(language);

        Game.resource.add(
            'lang',
            'resource/data/localizations/' + language + '.json'
        );

        Game.resource.add(
            'news_json',
            'resource/data/news/' + language + '.json'
        );

        await Game.resource.load();

        const languageData = {
            ...Game.resource.get('lang'),
        };

        Game.locale.setLanguageData(languageData);

        // Load player avatar once available (after loadingComplete).
        Game.resource.add(
            'player_avatar',
            Game.sdk.getAvatar(),
            ResourceDriver.TERTIARY
        );

        const canSubscribeBot = await Game.sdk.canSubscribeBot();
        if (canSubscribeBot) {
            // Do not await, allow game to preload in the background.
            Game.sdk.subscribeBot().then(result => {
                if (result) {
                    Game.sdk.gameTracking(FBTrackingEvent.Bot_confirm);
                } else {
                    Game.sdk.gameTracking(FBTrackingEvent.Bot_deny);
                }
            });
        }

        const canCreateShortcut = await Game.sdk.canCreateShortcut();

        setServerDate(parseInt(await Game.sdk.getServerTime()));

        this.gameLoadedState = {
            userState: {
                userId: Game.sdk.getUserId(),
                username: Game.sdk.getUsername(),
                userAvatar: Game.sdk.getAvatar(),
                createShortcut: canCreateShortcut
                    ? ShortcutStatus.ENABLED
                    : ShortcutStatus.DISABLED,
            },
            platform: {
                adsInterstitial: Game.sdk.hasInterstitialAdSupport(),
                adsRewarded: Game.sdk.hasRewardedAdSupport(),
                switchGame: Game.sdk.canSwitchGame(),
                subscribeBot: canSubscribeBot,
                crossPromoSupport: Game.sdk.hasCrossPromoSupport(),
            },
            playerState: Game.player.data.getPlayerState(),
            restoredPlayerState: restoredPlayerState,
            entryPoint: Game.sdk.getEntryPoint(),
        };

        if (
            !this.gameLoadedState.playerState.gdprCompliance &&
            (await Game.sdk.driver.shouldShowGDPR())
        ) {
            this.showGDPR = true;
        }
    }

    handleStateChanged(state: IGameState): void {
        if (!this.isActive()) return;

        if (
            state.UIState.popupID !== 'gdprMenu' &&
            state.previousState.UIState.popupID === 'gdprMenu' &&
            !state.gameLoaded
        ) {
            this.startGame();
        }
    }

    protected onStart(): void {
        Game.state.dispatch(GameActions.setLoadedData(this.gameLoadedState));
        Game.player.data.start();

        if (this.showGDPR) {
            Game.state.dispatch(UIActions.showPopup('gdprMenu'));
        } else {
            this.startGame();
        }
    }

    protected onDestroy(): void {}

    protected onLateUpdate(): void {}

    protected onStop(): void {}

    protected onUpdate(): void {}

    private startGame() {
        // Game.state.dispatch(GameActions.gameLoaded());
        Game.sdk.loadingComplete();
    }

    /**
     * Prepare IAP and resolve un-consumed purchases.
     * This must happen after the initial `gameLoaded` action to prevent unconsumed purchases from being overwritten by
     * initial load data.
     */
    public async prepareIAP() {
        Game.state.dispatch(GameActions.enableIAP([]));
        await Game.sdk.fetchCatalog();
        Game.state.dispatch(GameActions.readyIAP());
    }
}
